import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Grid } from "@/comn/components";
import { comnUtils } from "@/comn/utils"; // 프로젝트 공통 유틸
import { useTranslation } from "react-i18next";
import { Page, Group, Layout, Button, IconButton } from "@/comn/components"; // 화면 구성 컴포넌트
import { useForm, useFetch, useResource, useAuth, useGrid, useStore, useToast, usePage, useModal } from "@/comn/hooks"; // hook
import { URLS, APIS, BASE, SG_RCPT_LIST, SF_RCPT_LIST_SRCH, SF_TOT_AMT } from "./services/OsciRcptService"; // 서비스
import dayjs from "dayjs";

/*
 * Receipt List
 * !== 수납 목록 ==!
 */
export const OSCI0402001Q = (props: any) => {
    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */

    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "OSC0058" },
            { area: "comnCd", comnCd: "OSC0065" },
            { area: "comnCd", comnCd: "OSC0023" },
            { area: "comnCd", comnCd: "OSC0036" },
            { area: "coCd" },
            { area: "cstmCd" },
            { area: "bnkCd" },
        ],
    });

    const [vsbSrchDownBtn, setVsbSrchDownBtn] = useState(true);
    const [vsbSrchUpBtn, setVsbSrchUpBtn] = useState(false);
    const [vsbSrchCond, setVsbSrchCond] = useState(false);
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!

    const { pgeUid, menuId } = usePage({
        auth: ["auth_osci0003", "auth_osci0021"],
    }); // Page Unique identifier !== 화면 고유 식별자 ==!
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!\
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const auth = useAuth(); // Login , User Information Hook !== 로그인, 유저정보 Hook ==!

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Receipt Search !== 수납 검색 ==!
        rcptSrch: useForm({
            defaultSchema: SF_RCPT_LIST_SRCH,
            defaultValues: {
                ...(pgeStore?.form || { payMetdCd: ["A", "B", "C", "M", "N", "S", "T", "U"], payYn: "Y" }),
            },
        }),
        totAmt: useForm({
            defaultSchema: SF_TOT_AMT,
            defaultValues: {},
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        // Receipt List !== 수납 목록 ==!
        rcptList: useGrid({
            defaultSchema: SG_RCPT_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Get Receipt List !== 수납 목록 조회 ==!
        getRcptList: useFetch({
            api: (page = grid.rcptList.page) => {
                let data = {
                    ...form.rcptSrch.getValues(),
                    orgCd: auth.get("orgCd"),
                };

                if (data.bllNo) {
                    data.bllNo = data.bllNo.trim(); // 앞뒤 공백 제거
                    // Bill No.가 "X-XXXX-XX-XXXXXXX-X"(bllMgmtId 마스크) 패턴이면 '-' 제거 후 조회
                    if (/^[A-Za-z0-9]-[A-Za-z0-9]{4}-[A-Za-z0-9]{2}-[A-Za-z0-9]{7}-[A-Za-z0-9]$/.test(data.bllNo)) {
                        data.bllNo = data.bllNo.replaceAll("-", "");
                    }
                }
                return APIS.getRcptList(data, page, grid.rcptList.size);
            },
            enabled: comnUtils.isEmpty(form.rcptSrch.errors) && form.rcptSrch.isSubmitted,
            key: [grid.rcptList.page, grid.rcptList.size],
            onSuccess: (data) => {
                setStore(pgeUid, {
                    form: form.rcptSrch.getValues(),
                    page: grid.rcptList.page,
                    size: grid.rcptList.size,
                });

                form.totAmt.setValue("totAmt", data.totAmt.content);
            },
            onError: () => {},
            showToast: true,
        }),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Get Receipt List !== 수납 목록 조회 ==!
        getRcptList: (page: number = 0) => {
            // Validation Check !== 밸리데이션 체크 ==!
            if (!dateValidate()) {
                return false;
            }

            form.rcptSrch.handleSubmit(
                (data) => {
                    grid.rcptList.setPage(page);
                    fetch.getRcptList.fetch(page);
                },
                (data) => {
                    toast.showToast({ type: "warning", content: "msg.osc.00003" });
                },
            )();
        },
        // UP Button Control !== 위 버튼 제어 ==!
        upBtnCtrl: () => {
            setVsbSrchUpBtn(false);
            setVsbSrchDownBtn(true);
            setVsbSrchCond(false);
        },
        // Down Button Control !== 아래 버튼 제어 ==!
        downBtnCtrl: () => {
            setVsbSrchUpBtn(true);
            setVsbSrchDownBtn(false);
            setVsbSrchCond(true);
        },
    };

    const dateValidate = () => {
        const srchData = form.rcptSrch.getValues();
        const limit = 31;
        let result: boolean = true;

        // 수납일자: 최대 1년 (윤년이 끼어도 같은 날짜까지 허용하도록 일수 대신 1년 뒤 날짜와 비교)
        const rcptRgsrDiffDay = dayjs(srchData.rcptRgsrEndDt).diff(dayjs(srchData.rcptRgsrStrtDt), "day"); // 조회기간(day, week, month, year)
        if (dayjs(srchData.rcptEndDt).isAfter(dayjs(srchData.rcptStrtDt).add(1, "year"))) {
            modal.openModal({
                content: t("msg.osc.00336", { 0: t("L_RCPT_DT") }), // For Receipt Date, the search period cannot exceed 1 year.
            });
            result = false;
        }
        if (rcptRgsrDiffDay > limit) {
            modal.openModal({
                content: t("msg.osc.00183", { 0: t("L_RCPT_RGSR_DT"), 1: t("L_STRT_DT"), 2: limit }), // For Receipt Register Date, please enter period between Start Date to 31 day.
            });
            result = false;
        }

        // 고지 일자
        if (srchData.bllStrtDt || srchData.bllEndDt) {
            if (!srchData.bllStrtDt) {
                modal.openModal({
                    content: t("msg.osc.00051", { 0: t("L_BLL") + " " + t("L_STRT_DT") }), // Please enter Bill Start Date.
                });
                result = false;
            } else if (!srchData.bllEndDt) {
                modal.openModal({
                    content: t("msg.osc.00051", { 0: t("L_BLL") + " " + t("L_END_DT") }), // Please enter Bill End Date.
                });
                result = false;
            } else if (dayjs(srchData.bllEndDt).isAfter(dayjs(srchData.bllStrtDt).add(1, "year"))) {
                // 고지일자는 최대 1년 (윤년이 끼어도 같은 날짜까지 허용하도록 일수 대신 1년 뒤 날짜와 비교)
                modal.openModal({
                    content: t("msg.osc.00336", { 0: t("L_BLL_DT") }), // For Bill Date, the search period cannot exceed 1 year.
                });
                result = false;
            }
        }
        return result;
    };

    const render = {
        grid_rcptList: {
            head: {},
            cell: {
                bllNo: (props: any) => {
                    const { binding, rowValues, value } = props;
                    return (
                        <Layout direction="col" gap={1}>
                            <span style={{ color: "blue" }}>
                                <Link
                                    to={`${URLS.osci0402002q}/${rowValues.bllTpCd}-${rowValues.bllSysmCd}-${rowValues.bllYy}-${rowValues.bllSrno}-${rowValues.bllDgr}/collection/OSCI0402001Q`}
                                >{`${rowValues.bllNo}`}</Link>
                            </span>
                        </Layout>
                    );
                },
                tansadNo: (props: any) => {
                    const { binding, rowValues, value } = props;

                    if (rowValues.reffKeyTpCd == "T") {
                        return (
                            <Layout direction="col" gap={1}>
                                <span style={{ color: "blue" }}>
                                    <Link
                                        to={`${URLS.osci0401003q}/${rowValues.reffKeyNo1}-${rowValues.reffKeyNo2}-${rowValues.reffKeyNo3}/OSCI0402001Q`}
                                    >{`${rowValues.reffKeyNo1}-${rowValues.reffKeyNo2}-${rowValues.reffKeyNo3}`}</Link>
                                </span>
                            </Layout>
                        );
                    } else if (rowValues.tansadNo != null && rowValues.tansadNo.length > 6) {
                        const reffKeyNo1 = rowValues.tansadNo.substring(0, 4);
                        const reffKeyNo2 = rowValues.tansadNo.substring(4, 6);
                        const reffKeyNo3 = rowValues.tansadNo.substring(6);

                        console.log(`${reffKeyNo1}-${reffKeyNo2}-${reffKeyNo3}`);
                        return (
                            <Layout direction="col" gap={1}>
                                <span style={{ color: "blue" }}>
                                    <Link
                                        to={`${URLS.osci0401003q}/${reffKeyNo1}-${reffKeyNo2}-${reffKeyNo3}`}
                                    >{`${reffKeyNo1}-${reffKeyNo2}-${reffKeyNo3}`}</Link>
                                </span>
                            </Layout>
                        );
                    }
                },
            },
            row: (row: any) => {
                row.rcptBnkCd = row.rcptBnkNm ? row.rcptBnkCd + " [" + row.rcptBnkNm + "]" : row.rcptBnkCd;

                return true;
            },
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        // handler.getRcptList(pgeStore?.page);
        form.totAmt.setEditable(false);
        form.rcptSrch.setValue("srchDtTp", "rcptDtmRnge");
    }, []);

    return (
        <Page>
            <form>
                <Group>
                    <Group.Body>
                        <Group.Section>
                            <Group.Row>
                                <Group.Label {...form.rcptSrch.schema.srchDtTp} />
                                <Group.Col colSize={10}>
                                    {/* Date of Departure|Date of Registration !==  Date of Departure|Date of Registration ==! */}
                                    <Group.Control {...form.rcptSrch.schema.rcptDtmRnge} controlSize={10} />
                                </Group.Col>
                            </Group.Row>
                        </Group.Section>
                        {vsbSrchCond && (
                            <Group.Section>
                                <Group.Row>
                                    {/* Bill Number !== 고지서 번호 ==!  */}
                                    <Group.Control {...form.rcptSrch.schema.bllNo}></Group.Control>
                                    {/* TANSAD Number !== TANSAD 번호 ==!  */}
                                    <Group.Control {...form.rcptSrch.schema.tansadNo}></Group.Control>
                                </Group.Row>
                                <Group.Row>
                                    {/* Receipt Custom Office Code !== 고지서 세관 코드 ==!  */}
                                    <Group.Control {...form.rcptSrch.schema.cstmOfceCd}></Group.Control>
                                    {/* Payer Menagement Number !== 납부자 관리 번호 ==!  */}
                                    <Group.Control {...form.rcptSrch.schema.pyerMgmtNo}></Group.Control>
                                </Group.Row>
                                <Group.Row>
                                    {/* Bill Date !== 고지서 일자 ==!  */}
                                    <Group.Control {...form.rcptSrch.schema.bllDtmRnge}></Group.Control>
                                </Group.Row>
                                <Group.Row>
                                    {/* Payment Y/N !== 지불 여부 ==!  */}
                                    <Group.Control {...form.rcptSrch.schema.payYn}></Group.Control>
                                    {/* Declarant TIN !== 신고인 TIN ==!  */}
                                    <Group.Control {...form.rcptSrch.schema.dcltTin}></Group.Control>
                                </Group.Row>
                                <Group.Row>
                                    {/* Control Number !== 컨트롤 번호 ==!  */}
                                    <Group.Control {...form.rcptSrch.schema.ctrlNo}></Group.Control>

                                    {/* Clearance Plan Code !== 통관 계획 코드 ==!  */}
                                    <Group.Control {...form.rcptSrch.schema.csclPlnCd}></Group.Control>
                                </Group.Row>
                            </Group.Section>
                        )}
                        <Layout align="center">
                            {vsbSrchDownBtn && (
                                <IconButton
                                    icon="down"
                                    size="xl"
                                    onClick={() => {
                                        handler.downBtnCtrl();
                                    }}
                                />
                            )}
                            {vsbSrchUpBtn && (
                                <IconButton
                                    icon="up"
                                    size="xl"
                                    onClick={() => {
                                        handler.upBtnCtrl();
                                    }}
                                />
                            )}
                        </Layout>

                        <Layout direction="row">
                            <Layout.Left>
                                <Button
                                    role="reset"
                                    onClick={() => {
                                        form.rcptSrch.reset();
                                    }}
                                ></Button>
                            </Layout.Left>
                            <Layout.Right>
                                <Button
                                    role="search"
                                    onClick={() => {
                                        handler.getRcptList();
                                    }}
                                ></Button>
                            </Layout.Right>
                        </Layout>
                    </Group.Body>
                </Group>
            </form>

            <Group>
                <Group.Body>
                    <Layout direction="row">
                        <Layout.Left>
                            <Button
                                role="excelDown"
                                onClick={() => {
                                    grid.rcptList.exportExcel({
                                        /* 다운로드 엑셀 파일명 (확장자 없이), 기본값 Export(년월일시분초) */
                                        excelName: dayjs().format("YYYY-MM-DD HH:mm:ss") + "_" + pgeUid,
                                        /* 다운로드 엑셀 시트명 , 기본값 Sheet */
                                        sheetName: "Sheet Name",
                                    });
                                }}
                            ></Button>
                            <Button
                                onClick={() => {
                                    form.rcptSrch.handleSubmit(
                                        (data) => {
                                            // Validation Check !== 밸리데이션 체크 ==!
                                            if (!dateValidate()) {
                                                return false;
                                            }
                                            comnUtils.exportExcel({
                                                url: `${BASE.api}/rcptExc`,
                                                data: { ...form.rcptSrch.getValues() },
                                                fileName: "Notice Receipt List",
                                            });
                                        },
                                        (data) => {
                                            toast.showToast({ type: "warning", content: "msg.osc.00003" });
                                        },
                                    )();
                                }}
                            >
                                {t("B_DTL_DWNL")}
                            </Button>
                        </Layout.Left>
                    </Layout>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.totAmt.schema.totAmt}></Group.Control>
                        </Group.Row>
                    </Group.Section>
                    <Grid
                        {...grid.rcptList.grid}
                        data={fetch.getRcptList.data?.rcptList}
                        render={render.grid_rcptList}
                    />
                </Group.Body>
            </Group>
            <Layout direction="row">
                <Layout.Left></Layout.Left>
            </Layout>
        </Page>
    );
};
